import React, { Suspense, lazy, useCallback, useState } from "react"

import type { EngagementData, EngagementDrill } from "../../../components/widgets/types"
import { adaptResolved, indexCatalog } from "../components/Portfolio/widgetsResponseAdapter"
import type { PortfolioWindow } from "../redux/portfolio.types"
import type { ResolvedWidget } from "../redux/widgets.types"

// Loaded on the first click: the pop-up brings the whole leads table with it,
// which no dashboard needs until someone opens a count.
const LeadsDrillModal = lazy(() =>
  import("../../Leads/components/LeadsDrillModal").then((module) => ({
    default: module.LeadsDrillModal
  }))
)

export const ENGAGEMENT_METRIC = "lead_engagement"

export const ENGAGEMENT_INSTANCE = {
  type: "chart",
  dataSource: "leads",
  config: { metric: ENGAGEMENT_METRIC }
} as const

/** The filters a count was taken under, as the leads list reads them: the
 *  page's own as `dash_*`, a report widget's own as `wdash_*`. */
export const prefixFilterParams = (
  prefix: "dash_" | "wdash_",
  params: Record<string, string>
): Record<string, string> =>
  Object.fromEntries(Object.entries(params).map(([key, value]) => [`${prefix}${key}`, value]))

interface ResolveResponse {
  ok: boolean
  data?: unknown
}

/** One table's surroundings: how to resolve a span under its filters, and the
 *  same filters as leads-list parameters for the drill. */
export interface EngagementScope {
  resolve: (window: PortfolioWindow) => Promise<ResolveResponse>
  drillFilters?: Record<string, string>
}

export interface EngagementHandlers {
  onEngagementDrill: (drill: EngagementDrill) => void
  loadEngagementRange: (start: string, end: string) => Promise<EngagementData | null>
}

const unwrap = (data: unknown): ResolvedWidget | undefined =>
  (data as { details?: { data?: ResolvedWidget } } | undefined)?.details?.data ??
  (data as ResolvedWidget | undefined)

/** What a page hosting the Leads Added vs. Engaged table supplies it. `bind`
 *  turns a scope into the table's handlers; a caller memoises the result on
 *  whatever makes a new scope (filters, a refresh), because a new loader is
 *  what tells the table to read its spans again. Render `drillModal` once. */
export const useEngagementWidgetHost = () => {
  const [drill, setDrill] = useState<EngagementDrill | null>(null)

  const bind = useCallback(
    (scope: EngagementScope): EngagementHandlers => ({
      onEngagementDrill: (picked) =>
        setDrill(
          !picked.companyWide && scope.drillFilters && Object.keys(scope.drillFilters).length
            ? { ...picked, filters: { ...scope.drillFilters, ...(picked.filters ?? {}) } }
            : picked
        ),
      loadEngagementRange: async (start, end) => {
        const response = await scope.resolve({ preset: "custom", start, end } as PortfolioWindow)
        const resolved = response.ok ? unwrap(response.data) : undefined
        if (!resolved?.payload) return null
        return adaptResolved(resolved, indexCatalog(null)).payload.chart?.engagement ?? null
      }
    }),
    []
  )

  const drillModal = drill ? (
    <Suspense fallback={null}>
      <LeadsDrillModal drill={drill} onClose={() => setDrill(null)} />
    </Suspense>
  ) : null

  return { bind, drillModal }
}
